import { Alert, Form, Input, Modal } from 'antd';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useSubmission } from '../../../../../shared/hooks/use-submission';

interface ProposalReasonModalProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  isDanger?: boolean;
  /** Does the request and shows its own toast; a throw is shown inline, here, and keeps the modal open. */
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}

/** Every step on a proposal (submit, withdraw, approve, reject) is audited with a reason, so each asks for one. */
export function ProposalReasonModal({ open, title, confirmLabel, isDanger = false, onConfirm, onClose }: ProposalReasonModalProps) {
  const [form] = Form.useForm<{ reason: string }>();
  const { error, submitting, submit, clearError } = useSubmission();

  async function confirm({ reason }: { reason: string }) {
    const done = await submit(
      () => onConfirm(reason).then(() => true),
      (err) => apiErrorMessage(err) ?? 'That did not go through. Try again.',
    );
    if (!done) return;
    form.resetFields();
    onClose();
  }

  return (
    <Modal
      open={open}
      title={title}
      okText={confirmLabel}
      okButtonProps={{ danger: isDanger, loading: submitting }}
      onOk={() => form.submit()}
      onCancel={onClose}
      afterOpenChange={clearError}
      destroyOnHidden
    >
      {error && <Alert className="mb-4" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={confirm} disabled={submitting}>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
          <Input.TextArea rows={3} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
